import clsx from 'clsx';
import type { ReactNode } from 'react';

import { useHasRoomForTable } from '../hooks/useMediaQuery.ts';

/**
 * A generic responsive table: a real `<table>` above its own declared breakpoint, a separate card
 * list below it — never both, never a squeezed table scrolling sideways.
 *
 * Generalised from `routes/AccessPage.tsx`'s `CapabilityTable`/`CapabilityCards`, which is the
 * pattern this copies (same markup, same tokens, same dual-DOM-avoidance reasoning), once this
 * task needed the identical shape six more times over — patients, problems, allergies, medications,
 * immunizations, and vitals. `claude-kit/rules/invariants.md`'s "Favor the general, reusable
 * solution once a pattern actually recurs": AccessPage's table was the first occurrence and stayed
 * bespoke correctly at the time; a seventh copy of the same table/cards split is the point that rule
 * says to extract instead.
 *
 * **Every column declares its own wrap strategy**, per `invariants.md`'s "Table columns need a
 * deliberate strategy for long or variable-length content" — there is no default that silently
 * lets a column overflow. `'wrap'` (the default) is `AccessPage`'s own `whitespace-normal
 * break-words`, correct for text with natural spaces; `'anywhere'` adds `overflow-wrap: anywhere`
 * for a field with no guaranteed break point at all — an MRN, a hash, an id; `'nowrap'` is only for
 * a column whose content is inherently short and must never wrap (a status word, a short date).
 *
 * **The wrap CSS alone does not force a column to wrap — `maxWidthCh` is what actually constrains
 * it, and a column carrying unbounded or user-entered content needs both.** Found live, not
 * theorised: a real-browser check seeded a 64-character unbroken MRN into the patient table and
 * measured the *table's own* `scrollWidth` overflowing its container even with `wrap: 'anywhere'`
 * set — a `<table>` under `table-layout: auto` still sizes a column to its content's preferred
 * width unless something caps it, the same reason `AccessPage`'s own capability column pairs
 * `break-words whitespace-normal` with an explicit `max-w-[16ch]` rather than relying on the wrap
 * classes by themselves. `maxWidthCh` is that cap here, in `ch` units per `invariants.md`'s own
 * guidance, applied as an inline style rather than a Tailwind arbitrary-value class for the same
 * JIT-scanning reason `minWidthRem` below already documents. A column expected to carry unbounded
 * content should set both `wrap: 'anywhere'` (or `'wrap'`) *and* `maxWidthCh` — one is presentation,
 * the other is the constraint presentation needs to act on.
 *
 * No horizontal-scroll wrapper is added around the desktop table, deliberately: the whole point of
 * per-column wrapping (now with a real width to wrap against) is that the table never needs to grow
 * past `minWidthRem`, so a scrollbar appearing here would be silently masking a wrap failure rather
 * than a legitimate escape hatch — the same reasoning `AccessPage`'s own table already follows.
 */

export type DataTableWrap = 'nowrap' | 'wrap' | 'anywhere';

export interface DataTableColumn<T> {
  readonly key: string;
  readonly header: string;
  readonly cell: (row: T) => ReactNode;
  /** Defaults to `'wrap'`. */
  readonly wrap?: DataTableWrap;
  /** Caps the desktop `<th>`/`<td>`'s width, in `ch` units, so `wrap`/`'anywhere'` has something to
   * wrap against — see this module's own header comment for why the wrap classes alone do not
   * constrain a `<table>` column under `table-layout: auto`. Unset means uncapped: fine for a
   * `'nowrap'` column, or one whose content this project fully controls the shape of. */
  readonly maxWidthCh?: number;
  readonly cellClassName?: string;
  /** Card mode only: omit the "Header: " prefix — for a card's own heading or a trailing action. */
  readonly cardHidesLabel?: boolean;
}

export interface DataTableProps<T> {
  /** The table's accessible name (`<caption>`, visually hidden — every column header is visible). */
  readonly caption: string;
  readonly columns: readonly DataTableColumn<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
  /** This table's own declared minimum width, in rem. Drives `useHasRoomForTable`'s breakpoint. */
  readonly minWidthRem?: number;
  readonly emptyMessage: string;
}

const WRAP_CLASS: Record<DataTableWrap, string> = {
  nowrap: 'whitespace-nowrap',
  wrap: 'whitespace-normal break-words',
  anywhere: 'whitespace-normal break-words [overflow-wrap:anywhere]',
};

export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  minWidthRem = 34,
  emptyMessage,
}: DataTableProps<T>) {
  // Always called, never conditionally — the branch below on `rows.length` must not change how
  // many hooks this component calls between renders.
  const hasRoomForTable = useHasRoomForTable(minWidthRem);

  if (rows.length === 0) {
    return <p className="m-0 text-sm text-ink-muted">{emptyMessage}</p>;
  }

  return hasRoomForTable ? (
    <DesktopTable
      caption={caption}
      columns={columns}
      rows={rows}
      rowKey={rowKey}
      minWidthRem={minWidthRem}
    />
  ) : (
    <CardList columns={columns} rows={rows} rowKey={rowKey} />
  );
}

function DesktopTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  minWidthRem,
}: {
  readonly caption: string;
  readonly columns: readonly DataTableColumn<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
  readonly minWidthRem: number;
}) {
  return (
    <table
      className="w-full border-collapse text-left text-sm"
      // An inline style, not a Tailwind arbitrary-value class: `minWidthRem` is a runtime value
      // chosen per caller, and Tailwind's JIT scanner only generates CSS for class strings that
      // appear literally in source — a template-literal class name here would silently produce no
      // CSS at all in production. A raw style has no such scanning step.
      style={{ minWidth: `${String(minWidthRem)}rem` }}
    >
      <caption className="sr-only">{caption}</caption>
      <thead>
        <tr className="bg-table-head">
          {columns.map((column, index) => (
            <th
              key={column.key}
              scope="col"
              className={clsx(
                'px-3 py-2 font-semibold text-ink',
                index === 0 && 'rounded-l-control',
                index === columns.length - 1 && 'rounded-r-control',
              )}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={rowKey(row)} className="border-b border-row-line last:border-b-0">
            {columns.map((column, index) => {
              const cellClass = clsx(
                'px-3 py-3 align-top text-ink',
                WRAP_CLASS[column.wrap ?? 'wrap'],
                column.cellClassName,
              );
              const cellStyle =
                column.maxWidthCh === undefined
                  ? undefined
                  : { maxWidth: `${String(column.maxWidthCh)}ch` };
              // The first column is the row's header, matching `AccessPage`'s own table: a screen
              // reader announces it before every other cell in the row, so it should be whatever
              // most identifies the row (an MRN, a description) rather than an incidental field.
              return index === 0 ? (
                <th
                  key={column.key}
                  scope="row"
                  className={clsx(cellClass, 'font-normal')}
                  style={cellStyle}
                >
                  {column.cell(row)}
                </th>
              ) : (
                <td key={column.key} className={cellClass} style={cellStyle}>
                  {column.cell(row)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The narrow layout: separate markup, not the same table squeezed — matching `AccessPage`'s own
 * `CapabilityCards` and the measurement in its header comment (a table scrolling sideways at phone
 * width showed 59% of every row behind a gesture nobody's automated check would flag).
 */
function CardList<T>({
  columns,
  rows,
  rowKey,
}: {
  readonly columns: readonly DataTableColumn<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
}) {
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0">
      {rows.map((row) => (
        <li key={rowKey(row)} className="rounded-control border border-line p-3 text-sm">
          {columns.map((column) => (
            <div
              key={column.key}
              className={clsx('mt-1.5 first:mt-0', WRAP_CLASS[column.wrap ?? 'wrap'])}
            >
              {column.cardHidesLabel === true ? null : (
                <span className="mr-1 text-ink-muted">{column.header}:</span>
              )}
              <span className="text-ink">{column.cell(row)}</span>
            </div>
          ))}
        </li>
      ))}
    </ul>
  );
}
